import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Plus, Lock, Unlock, Pencil, Trash2, Check, X, GripVertical, User as UserIcon, Bell } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ProjectTaskTemplatesDialog = ({ open, onOpenChange, onChanged }) => {
  const { user, getAuthHeader } = useAuth();
  const { t } = useTranslation();
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newOffset, setNewOffset] = useState('');
  const [newAssignee, setNewAssignee] = useState('__none__');
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editOffset, setEditOffset] = useState('');
  const [editAssignee, setEditAssignee] = useState('__none__');
  const isAdmin = user?.role === 'admin';

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/project-task-templates`, { headers: getAuthHeader() });
      setTemplates(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('templates.error'));
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, t]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/users`, { headers: getAuthHeader() });
      setUsers(r.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    if (open) { fetchTemplates(); fetchUsers(); }
  }, [open, fetchTemplates, fetchUsers]);

  const create = async () => {
    if (!newTitle.trim()) { toast.error(t('templates.titleRequired')); return; }
    try {
      await axios.post(`${API_URL}/project-task-templates`, {
        title: newTitle.trim(),
        description: newDesc.trim() || null,
        default_due_offset_days: newOffset === '' ? null : parseInt(newOffset, 10),
        default_assignee_id: newAssignee === '__none__' ? null : newAssignee,
        order: templates.length,
      }, { headers: getAuthHeader() });
      setNewTitle(''); setNewDesc(''); setNewOffset(''); setNewAssignee('__none__'); setCreatingOpen(false);
      toast.success(t('templates.created'));
      fetchTemplates();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('templates.error'));
    }
  };

  const beginEdit = (tpl) => {
    setEditingId(tpl.id);
    setEditTitle(tpl.title);
    setEditDesc(tpl.description || '');
    setEditOffset(tpl.default_due_offset_days == null ? '' : String(tpl.default_due_offset_days));
    setEditAssignee(tpl.default_assignee_id || '__none__');
  };

  const saveEdit = async (tpl) => {
    if (!editTitle.trim()) { toast.error(t('templates.titleRequired')); return; }
    try {
      await axios.put(`${API_URL}/project-task-templates/${tpl.id}`, {
        title: editTitle.trim(),
        description: editDesc.trim() || null,
        default_due_offset_days: editOffset === '' ? null : parseInt(editOffset, 10),
        default_assignee_id: editAssignee === '__none__' ? "" : editAssignee,
      }, { headers: getAuthHeader() });
      setEditingId(null);
      toast.success(t('templates.updated'));
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('templates.error'));
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(t('templates.confirmDelete'))) return;
    try {
      await axios.delete(`${API_URL}/project-task-templates/${tpl.id}`, { headers: getAuthHeader() });
      toast.success(t('templates.deleted'));
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('templates.error'));
    }
  };

  const toggleLock = async (tpl) => {
    try {
      const path = tpl.locked ? 'unlock' : 'lock';
      await axios.post(`${API_URL}/project-task-templates/${tpl.id}/${path}`, {}, { headers: getAuthHeader() });
      toast.success(tpl.locked ? t('templates.unlocked') : t('templates.locked'));
      fetchTemplates();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('templates.error'));
    }
  };

  const canModify = (tpl) => !tpl.locked || isAdmin;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl max-h-[85vh] overflow-y-auto"
        aria-describedby="templates-dialog-desc"
        data-testid="templates-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t('templates.title')}</DialogTitle>
          <p id="templates-dialog-desc" className="text-xs text-zinc-500 mt-1">{t('templates.subtitle')}</p>
        </DialogHeader>

        <div className="space-y-2 mt-4">
          {loading && templates.length === 0 && (
            <p className="text-sm text-zinc-400 text-center py-8">{t('common.loading')}</p>
          )}
          {!loading && templates.length === 0 && !creatingOpen && (
            <div className="text-center py-8 text-zinc-500 border-2 border-dashed border-zinc-200 rounded-xl">
              <p className="text-sm">{t('templates.empty')}</p>
            </div>
          )}
          {templates.map((tpl) => (
            <div
              key={tpl.id}
              className="border border-zinc-200 rounded-xl p-3 flex items-start gap-2 bg-white hover:bg-zinc-50/60 transition-colors"
              data-testid={`template-row-${tpl.id}`}
            >
              <GripVertical className="w-4 h-4 text-zinc-300 mt-1 flex-shrink-0" />
              {editingId === tpl.id ? (
                <div className="flex-1 space-y-2">
                  <Input value={editTitle} onChange={(e) => setEditTitle(e.target.value)} className="h-9" data-testid={`template-edit-title-${tpl.id}`} />
                  <Textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} placeholder={t('templates.descriptionPh')} className="resize-none text-sm" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={editOffset}
                        onChange={(e) => setEditOffset(e.target.value)}
                        placeholder={t('templates.offsetPh')}
                        className="h-9"
                      />
                      <span className="text-[10px] text-zinc-500 whitespace-nowrap">{t('templates.offsetHint')}</span>
                    </div>
                    <Select value={editAssignee} onValueChange={setEditAssignee}>
                      <SelectTrigger className="h-9 text-sm" data-testid={`template-edit-assignee-${tpl.id}`}>
                        <SelectValue placeholder={t('templates.assignPh')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('templates.assignNone')}</SelectItem>
                        {users.map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-full h-8">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" onClick={() => saveEdit(tpl)} className="rounded-full h-8 bg-[#D93025] hover:bg-[#B91C1C] text-white" data-testid={`template-save-${tpl.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> {t('common.save')}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm text-zinc-900 truncate">{tpl.title}</p>
                    {tpl.locked && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full">
                        <Lock className="w-2.5 h-2.5" />
                        {t('templates.lockedTag')}
                      </span>
                    )}
                    {tpl.default_assignee_id && (
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">
                        <UserIcon className="w-2.5 h-2.5" />
                        {tpl.default_assignee_name || '?'}
                      </span>
                    )}
                  </div>
                  {tpl.description && (
                    <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">{tpl.description}</p>
                  )}
                  {tpl.default_due_offset_days != null && (
                    <p className="text-[11px] text-zinc-400 mt-0.5">
                      {t('templates.dueAfter', { count: tpl.default_due_offset_days })}
                    </p>
                  )}
                </div>
              )}
              {editingId !== tpl.id && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => toggleLock(tpl)}
                      className={`p-1.5 rounded-lg transition-colors ${tpl.locked ? 'text-amber-600 hover:bg-amber-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
                      title={tpl.locked ? t('templates.unlock') : t('templates.lock')}
                      data-testid={`template-lock-${tpl.id}`}
                    >
                      {tpl.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  {canModify(tpl) && (
                    <>
                      <button
                        type="button"
                        onClick={() => beginEdit(tpl)}
                        className="p-1.5 rounded-lg text-zinc-500 hover:bg-zinc-100"
                        data-testid={`template-edit-${tpl.id}`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(tpl)}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"
                        data-testid={`template-delete-${tpl.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}

          {creatingOpen ? (
            <div className="border-2 border-[#D93025]/30 rounded-xl p-3 bg-red-50/30 space-y-2">
              <Input
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={t('templates.titlePh')}
                className="h-10"
                autoFocus
                data-testid="template-new-title"
              />
              <Textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                rows={2}
                placeholder={t('templates.descriptionPh')}
                className="resize-none text-sm"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    value={newOffset}
                    onChange={(e) => setNewOffset(e.target.value)}
                    placeholder={t('templates.offsetPh')}
                    className="h-9"
                  />
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">{t('templates.offsetHint')}</span>
                </div>
                <Select value={newAssignee} onValueChange={setNewAssignee}>
                  <SelectTrigger className="h-9 text-sm" data-testid="template-new-assignee">
                    <SelectValue placeholder={t('templates.assignPh')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('templates.assignNone')}</SelectItem>
                    {users.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-500 pt-1">
                <Bell className="w-3 h-3" />
                {t('templates.assignHint')}
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => { setCreatingOpen(false); setNewTitle(''); setNewDesc(''); setNewOffset(''); setNewAssignee('__none__'); }} className="rounded-full h-8">
                  {t('common.cancel')}
                </Button>
                <Button size="sm" onClick={create} className="rounded-full h-8 bg-[#D93025] hover:bg-[#B91C1C] text-white" data-testid="template-create-save">
                  <Check className="w-3.5 h-3.5 mr-1" /> {t('templates.add')}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setCreatingOpen(true)}
              className="w-full rounded-xl border-dashed border-2 border-zinc-200 h-11 text-zinc-500 hover:border-[#D93025] hover:text-[#D93025]"
              data-testid="template-add-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" />
              {t('templates.addNew')}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProjectTaskTemplatesDialog;
